import { useEffect, useRef, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { Clock } from "lucide-react";

// PATCH 36 Me6 — a graceful session-timeout warning. After a period of inactivity we show a
// dismissible countdown with a one-click "Stay signed in", instead of an abrupt mid-task logout.
const IDLE_MS = 25 * 60 * 1000;   // warn after 25 min of no activity
const GRACE_MS = 2 * 60 * 1000;   // 2 min to respond before we sign out

export default function SessionWatch() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const [warn, setWarn] = useState(false);
  const [left, setLeft] = useState(GRACE_MS);
  const idleTimer = useRef(null);
  const graceTimer = useRef(null);
  const tick = useRef(null);

  const clearAll = () => { [idleTimer, graceTimer, tick].forEach((t) => t.current && clearInterval(t.current) & clearTimeout(t.current)); };

  const startGrace = useCallback(() => {
    setWarn(true); setLeft(GRACE_MS);
    tick.current = setInterval(() => setLeft((l) => Math.max(0, l - 1000)), 1000);
    graceTimer.current = setTimeout(async () => { await logout(); nav("/login"); }, GRACE_MS);
  }, [logout, nav]);

  const arm = useCallback(() => {
    clearTimeout(idleTimer.current);
    idleTimer.current = setTimeout(startGrace, IDLE_MS);
  }, [startGrace]);

  const onActivity = useCallback(() => { if (!warn) arm(); }, [warn, arm]);

  useEffect(() => {
    if (!user) return undefined;
    arm();
    const evts = ["mousemove", "keydown", "click", "scroll", "touchstart"];
    evts.forEach((e) => window.addEventListener(e, onActivity, { passive: true }));
    return () => { evts.forEach((e) => window.removeEventListener(e, onActivity)); clearAll(); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, onActivity, arm]);

  const extend = async () => {
    clearInterval(tick.current); clearTimeout(graceTimer.current);
    setWarn(false);
    try { await api.get("/auth/me"); } catch { /* still resets the local idle clock */ }
    arm();
  };

  if (!user || !warn) return null;
  const secs = Math.ceil(left / 1000);
  return (
    <div data-testid="session-timeout-warning" role="alertdialog" aria-modal="true"
      style={{ position: "fixed", inset: 0, background: "rgba(17,17,19,.6)", zIndex: 95,
        display: "flex", alignItems: "center", justifyContent: "center", padding: 20 }}>
      <div className="k-container k-fadein" style={{ width: 420, padding: 24 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 8 }}>
          <Clock size={18} aria-hidden="true" />
          <h3 className="k-heading" style={{ margin: 0, fontSize: 17 }}>Still there?</h3>
        </div>
        <p className="k-muted" style={{ fontSize: 13, lineHeight: 1.6 }}>
          For your security we'll sign you out in <strong className="k-heading" data-testid="session-countdown">{secs}s</strong> due
          to inactivity. Your work is auto-saved — but you can stay signed in.
        </p>
        <div style={{ display: "flex", gap: 10, justifyContent: "flex-end", marginTop: 16 }}>
          <button className="k-btn k-btn-ghost" data-testid="session-signout" onClick={async () => { await logout(); nav("/login"); }}>Sign out now</button>
          <button className="k-btn k-btn-primary" data-testid="session-extend" onClick={extend}>Stay signed in</button>
        </div>
      </div>
    </div>
  );
}
